import {
  catalogReportId,
  REPORT_COLUMNS,
  type CatalogReportId,
  type DebtsReportResponse,
  type PurchasesAnalysisResponse,
  type SalesAnalysisResponse,
  type TabularReportResponse,
  type TreasuryMovementResponse,
} from '@hesabat/contracts';
import type { CsvCell } from './csv';

/** التقارير الجدولية من الفهرس: أعمدتها فى العقد، وتعرضها الواجهة بمكوّن واحد. */
export type TabularReport = CatalogReportId | 'audit-log';

export type CenterReport = 'sales' | 'purchases' | 'treasury' | 'debts' | TabularReport;

export const TABULAR_REPORTS: readonly TabularReport[] = [...catalogReportId.options, 'audit-log'];

export function isTabular(report: CenterReport): report is TabularReport {
  return (TABULAR_REPORTS as readonly string[]).includes(report);
}

/** تقارير الفهرس التى طرفها مورد — الباقى طرفه عميل. */
const SUPPLIER_REPORTS: readonly TabularReport[] = [
  'purchase-bills',
  'purchase-notes',
  'supplier-payments',
  'top-suppliers',
];

export function partyOf(report: TabularReport): 'customer' | 'supplier' | null {
  if (SUPPLIER_REPORTS.includes(report)) return 'supplier';
  if (['sales-invoices', 'sales-notes', 'customer-receipts', 'unapplied-receipts'].includes(report))
    return 'customer';
  return null;
}

/**
 * آخر اختيارات المستخدم فى مركز التقارير. كلها نصوص كما تُرسل فى الاستعلام؛ الفارغ
 * يعنى «الكل». تُحفظ لكل مستخدم وشركة فى المتصفح فقط — تفضيل عرض، لا بيانات.
 */
export interface CenterFilters {
  report: CenterReport;
  from: string;
  to: string;
  asOf: string;
  salesGroupBy: 'day' | 'month' | 'customer' | 'item';
  purchasesGroupBy: 'day' | 'month' | 'supplier' | 'account';
  status: 'all' | 'open' | 'settled' | 'cancelled';
  customerId: string;
  itemId: string;
  supplierId: string;
  accountCode: string;
  treasuryAccountCode: string;
  party: 'customers' | 'suppliers';
  overdueOnly: boolean;
  minBalance: string;
  sort: 'largest' | 'overdue' | 'code';
  noteKind: 'all' | 'credit' | 'debit';
  sourceType: string;
  limit: string;
  action: string;
  entityType: string;
  /** للأصناف الراكدة: أيام بلا بيع. */
  days: string;
}

export function defaultFilters(today: string): CenterFilters {
  const year = today.slice(0, 4);
  return {
    report: 'sales',
    from: year + '-01-01',
    to: year + '-12-31',
    asOf: today,
    salesGroupBy: 'month',
    purchasesGroupBy: 'month',
    status: 'all',
    customerId: '',
    itemId: '',
    supplierId: '',
    accountCode: '',
    treasuryAccountCode: '',
    party: 'customers',
    overdueOnly: false,
    minBalance: '',
    sort: 'largest',
    noteKind: 'all',
    sourceType: '',
    limit: '10',
    action: '',
    entityType: '',
    days: '90',
  };
}

const ENUMS: { [K in keyof CenterFilters]?: readonly string[] } = {
  report: ['sales', 'purchases', 'treasury', 'debts', ...TABULAR_REPORTS],
  noteKind: ['all', 'credit', 'debit'],
  salesGroupBy: ['day', 'month', 'customer', 'item'],
  purchasesGroupBy: ['day', 'month', 'supplier', 'account'],
  status: ['all', 'open', 'settled', 'cancelled'],
  party: ['customers', 'suppliers'],
  sort: ['largest', 'overdue', 'code'],
};

export function storageKey(userId: string, companyId: string): string {
  return 'hesabat.reportsCenter.' + userId + '.' + companyId;
}

/**
 * يقرأ المحفوظ ويُسقط ما لا يصح منه حقلاً حقلاً: نسخة قديمة أو تخزين معطوب لا يكسر
 * الشاشة، والحقل الغائب يأخذ قيمته الافتراضية.
 */
export function parseFilters(raw: string | null, defaults: CenterFilters): CenterFilters {
  if (!raw) return defaults;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaults;
  }
  if (typeof parsed !== 'object' || parsed === null) return defaults;
  const result: Record<string, unknown> = { ...defaults };
  for (const [key, fallback] of Object.entries(defaults)) {
    const value = (parsed as Record<string, unknown>)[key];
    if (typeof value !== typeof fallback) continue;
    const allowed = ENUMS[key as keyof CenterFilters];
    if (allowed && !allowed.includes(value as string)) continue;
    if (/^(from|to|asOf)$/.test(key) && !/^\d{4}-\d{2}-\d{2}$/.test(value as string)) continue;
    result[key] = value;
  }
  return result as unknown as CenterFilters;
}

export function loadFilters(key: string, defaults: CenterFilters): CenterFilters {
  try {
    return parseFilters(window.localStorage.getItem(key), defaults);
  } catch {
    return defaults;
  }
}

export function saveFilters(key: string, filters: CenterFilters): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // تخزين ممتلئ أو محظور — التفضيل يضيع ولا يتوقف التقرير
  }
}

export const STATUS_LABELS: Record<CenterFilters['status'], string> = {
  all: 'الكل',
  open: 'عليها متبقٍ',
  settled: 'مسددة بالكامل',
  cancelled: 'ملغاة',
};

export const SALES_COLUMNS = [
  ['invoices', 'الفواتير'],
  ['cancellations', 'الإلغاء'],
  ['creditNotes', 'إشعارات دائنة (مردودات وتخفيضات)'],
  ['debitNotes', 'إشعارات مدينة'],
  ['netSales', 'صافى المبيعات'],
  ['vat', 'القيمة المضافة'],
  ['total', 'الإجمالى بالضريبة'],
  ['collected', 'المحصّل'],
  ['outstanding', 'المتبقى'],
] as const;

export const PURCHASES_COLUMNS = [
  ['bills', 'فواتير الموردين'],
  ['cancellations', 'الإلغاء'],
  ['debitNotes', 'إشعارات مدينة (مرتجع وخصم)'],
  ['creditNotes', 'إشعارات دائنة'],
  ['netPurchases', 'صافى المشتريات'],
  ['vat', 'القيمة المضافة'],
  ['total', 'الإجمالى بالضريبة'],
  ['paid', 'المسدد'],
  ['outstanding', 'المتبقى'],
] as const;

export const TREASURY_COLUMNS = [
  ['openingBalance', 'رصيد أول الفترة'],
  ['receipts', 'الوارد'],
  ['payments', 'المنصرف'],
  ['transfersIn', 'تحويل وارد'],
  ['transfersOut', 'تحويل صادر'],
  ['closingBalance', 'رصيد آخر الفترة'],
] as const;

export const DEBTS_COLUMNS = [
  ['current', 'غير مستحق'],
  ['overdue', 'متأخر'],
  ['over90', 'منه أكثر من 90 يومًا'],
  ['unappliedCredit', 'تسوية غير مخصصة'],
  ['total', 'الرصيد'],
] as const;

export const TREASURY_CATEGORY_LABELS: Record<
  TreasuryMovementResponse['accounts'][number]['lines'][number]['category'],
  string
> = {
  customer_receipt: 'تحصيل من عميل',
  supplier_payment: 'سداد لمورد',
  expense: 'مصروف',
  treasury_transfer: 'تحويل',
  opening_balance: 'رصيد افتتاحى',
  other: 'أخرى',
};

/** عنوان المجموعة: الشهر بالعربية، وغيره كما جاء (اليوم أو اسم الطرف أو الصنف). */
const MONTHS = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

export function groupLabel(groupBy: string, row: { label: string; code: string | null }): string {
  if (groupBy === 'month' && /^\d{4}-\d{2}$/.test(row.label)) {
    return MONTHS[Number(row.label.slice(5)) - 1] + ' ' + row.label.slice(0, 4);
  }
  return row.code ? row.code + ' — ' + row.label : row.label;
}

type Analysis = SalesAnalysisResponse | PurchasesAnalysisResponse;

export function analysisCsv(
  report: Analysis,
  columns: typeof SALES_COLUMNS | typeof PURCHASES_COLUMNS,
): { header: string[]; rows: CsvCell[][] } {
  const figures = report.totals as Record<string, string | number | null>;
  const shown = columns.filter(([key]) => figures[key] !== null);
  const get = (row: object, key: string) => (row as Record<string, CsvCell>)[key];
  const countKey = 'invoiceCount' in report.totals ? 'invoiceCount' : 'billCount';
  return {
    header: ['المجموعة', 'عدد المستندات', ...shown.map(([, label]) => label)],
    rows: [
      ...report.rows.map((row) => [
        groupLabel(report.groupBy, row),
        get(row, countKey),
        ...shown.map(([key]) => get(row, key)),
      ]),
      ['الإجمالى', get(report.totals, countKey), ...shown.map(([key]) => get(report.totals, key))],
    ],
  };
}

export function treasuryCsv(report: TreasuryMovementResponse): {
  header: string[];
  rows: CsvCell[][];
} {
  const rows: CsvCell[][] = [];
  for (const account of report.accounts) {
    rows.push([account.accountCode + ' — ' + account.accountNameAr, '', '', '', '', '', '']);
    rows.push(['', 'رصيد أول الفترة', '', '', '', '', account.openingBalance]);
    for (const line of account.lines) {
      rows.push([
        line.postingDate,
        TREASURY_CATEGORY_LABELS[line.category],
        line.journalNumber,
        [line.counterparty, line.memo ?? line.reference].filter(Boolean).join(' — '),
        line.debit,
        line.credit,
        line.runningBalance,
      ]);
    }
    rows.push(['', 'رصيد آخر الفترة', '', '', '', '', account.closingBalance]);
  }
  rows.push([]);
  for (const [key, label] of TREASURY_COLUMNS)
    rows.push(['', 'إجمالى ' + label, report.totals[key]]);
  return {
    header: ['التاريخ', 'البيان', 'القيد', 'الطرف / الوصف', 'وارد', 'منصرف', 'الرصيد'],
    rows,
  };
}

export function debtsCsv(report: DebtsReportResponse): { header: string[]; rows: CsvCell[][] } {
  const party = report.party === 'customers' ? 'العميل' : 'المورد';
  return {
    header: ['الكود', party, ...DEBTS_COLUMNS.map(([, label]) => label)],
    rows: [
      ...report.rows.map((row) => [
        row.code,
        row.nameAr,
        ...DEBTS_COLUMNS.map(([key]) => row[key]),
      ]),
      ['', 'الإجمالى', ...DEBTS_COLUMNS.map(([key]) => report.totals[key])],
    ],
  };
}

/** وجهة تقرير قائم فى شاشة أخرى — نفس وجهات التطبيق، فالفهرس لا يكرر تقريرًا مبنيًا. */
export type CatalogTarget =
  | {
      kind: 'reports';
      tab: 'income' | 'balance' | 'general' | 'aging' | 'apAging' | 'vat' | 'withholding';
    }
  | { kind: 'ledger'; accountCode: null }
  | { kind: 'customers' }
  | { kind: 'suppliers' }
  | { kind: 'payments' }
  | { kind: 'inventory' }
  | { kind: 'periods' }
  | { kind: 'dashboard' };

/**
 * بند فى فهرس التقارير:
 * - `center`: يُعرض هنا بتصفيته وتجميعه وإجمالياته وطباعته وتصديره.
 * - `screen`: تقرير قائم يُفتح فى شاشته — لا نسخة ثانية منه.
 * - `planned`: فى الخطة، لم يُبنَ بعد.
 * - `blocked`: ينتظر قرار طريقة محاسبية من المالك (BLOCKED-ACCOUNTING) — لا يُبنى على تخمين.
 */
export type CatalogEntry = { id: string; title: string; hint: string } & (
  | { kind: 'center'; report: CenterReport }
  | { kind: 'screen'; target: CatalogTarget }
  | { kind: 'planned' }
  | { kind: 'blocked'; question: string }
);

export interface CatalogCategory {
  id: string;
  title: string;
  entries: CatalogEntry[];
}

const planned = (id: string, title: string, hint: string): CatalogEntry => ({
  id,
  title,
  hint,
  kind: 'planned',
});

const screen = (id: string, title: string, hint: string, target: CatalogTarget): CatalogEntry => ({
  id,
  title,
  hint,
  kind: 'screen',
  target,
});

export const CATALOG: CatalogCategory[] = [
  {
    id: 'sales',
    title: 'المبيعات',
    entries: [
      {
        id: 'sales-analysis',
        title: 'تحليل المبيعات',
        hint: 'بالعميل أو الصنف أو الشهر أو اليوم: الصافى والضريبة والتحصيل',
        kind: 'center',
        report: 'sales',
      },
      {
        id: 'sales-register',
        title: 'سجل الفواتير',
        hint: 'كل فاتورة بإشعاراتها ومحصّلها ومتبقيها وحالها',
        kind: 'center',
        report: 'sales-invoices',
      },
      {
        id: 'sales-notes-register',
        title: 'سجل الإشعارات',
        hint: 'الإشعارات الدائنة والمدينة بفاتورتها وسببها',
        kind: 'center',
        report: 'sales-notes',
      },
      {
        id: 'receipts-register',
        title: 'سجل التحصيل',
        hint: 'إيصالات التحصيل: النقد والخصم من المنبع والمخصص',
        kind: 'center',
        report: 'customer-receipts',
      },
      {
        id: 'unapplied-receipts',
        title: 'تحصيلات غير مخصصة',
        hint: 'دفعات لم تُسوَّ على فواتير فى تاريخ',
        kind: 'center',
        report: 'unapplied-receipts',
      },
      {
        id: 'top-customers',
        title: 'أكبر العملاء',
        hint: 'ترتيب العملاء بصافى المبيعات ونسبتهم',
        kind: 'center',
        report: 'top-customers',
      },
    ],
  },
  {
    id: 'purchases',
    title: 'المشتريات',
    entries: [
      {
        id: 'purchases-analysis',
        title: 'تحليل المشتريات',
        hint: 'بالمورد أو البند أو الشهر: الصافى والضريبة والسداد',
        kind: 'center',
        report: 'purchases',
      },
      {
        id: 'bills-register',
        title: 'سجل فواتير الموردين',
        hint: 'كل فاتورة بإشعاراتها ومسددها ومتبقيها وحالها',
        kind: 'center',
        report: 'purchase-bills',
      },
      {
        id: 'purchase-notes-register',
        title: 'سجل إشعارات الموردين',
        hint: 'المرتجعات والخصومات والزيادات بفاتورتها',
        kind: 'center',
        report: 'purchase-notes',
      },
      {
        id: 'payments-register',
        title: 'سجل السداد',
        hint: 'مدفوعات الموردين والخصم من المنبع والمخصص',
        kind: 'center',
        report: 'supplier-payments',
      },
      {
        id: 'top-suppliers',
        title: 'أكبر الموردين',
        hint: 'ترتيب الموردين بصافى المشتريات ونسبتهم',
        kind: 'center',
        report: 'top-suppliers',
      },
    ],
  },
  {
    id: 'debts',
    title: 'المديونية',
    entries: [
      {
        id: 'debts',
        title: 'ملخص المديونية',
        hint: 'من عليه فلوس ولمن علينا فى تاريخ — المتأخر وحد أدنى للرصيد',
        kind: 'center',
        report: 'debts',
      },
      screen('ar-aging', 'أعمار ديون العملاء', 'شرائح أيام التأخير', {
        kind: 'reports',
        tab: 'aging',
      }),
      screen('ap-aging', 'أعمار ديون الموردين', 'شرائح أيام التأخير', {
        kind: 'reports',
        tab: 'apAging',
      }),
      screen('customer-statement', 'كشف حساب عميل', 'من شاشة العملاء', { kind: 'customers' }),
      screen('supplier-statement', 'كشف حساب مورد', 'من شاشة الموردين', { kind: 'suppliers' }),
    ],
  },
  {
    id: 'treasury',
    title: 'الخزينة والبنوك',
    entries: [
      {
        id: 'cash-book',
        title: 'حركة النقدية',
        hint: 'لكل خزينة وبنك: افتتاحى ووارد ومنصرف وتحويلات وختامى بمستند كل سطر',
        kind: 'center',
        report: 'treasury',
      },
      planned('transfers-register', 'سجل التحويلات', 'التحويلات بين الخزائن والبنوك'),
      planned('expenses-by-category', 'المصروفات النقدية بالبند', 'تجميع المصروفات بحسابها'),
      planned('reconciliation-status', 'حالة المطابقة البنكية', 'آخر مطابقة والمعلق لكل حساب'),
    ],
  },
  {
    id: 'inventory',
    title: 'المخزون',
    entries: [
      screen('stock', 'الرصيد والتقييم وكارت الصنف', 'من شاشة المخزون', { kind: 'inventory' }),
      {
        id: 'stock-movement',
        title: 'حركة الأصناف لفترة',
        hint: 'لكل صنف: أول الفترة والوارد والصادر وآخرها كمية وقيمة',
        kind: 'center',
        report: 'stock-movement',
      },
      {
        id: 'slow-moving',
        title: 'الأصناف الراكدة',
        hint: 'أصناف عليها رصيد ولم يُبَع منها شىء منذ عدد أيام تختاره',
        kind: 'center',
        report: 'slow-moving',
      },
      {
        id: 'gross-profit-by-item',
        title: 'مجمل الربح بالصنف',
        hint: 'صافى مبيعات كل صنف ناقص تكلفته، وهامش الربح',
        kind: 'center',
        report: 'gross-profit-by-item',
      },
    ],
  },
  {
    id: 'ledger',
    title: 'الحسابات العامة',
    entries: [
      screen('trial-balance', 'ميزان المراجعة', 'فى الصفحة الرئيسية', { kind: 'dashboard' }),
      {
        id: 'trial-balance-movement',
        title: 'ميزان المراجعة بالأرصدة والحركة',
        hint: 'لكل حساب: رصيد أول الفترة وحركتها ورصيد آخرها مدينًا ودائنًا',
        kind: 'center',
        report: 'trial-balance-movement',
      },
      screen('general-ledger', 'الأستاذ العام', 'كشف كل حساب له حركة', {
        kind: 'reports',
        tab: 'general',
      }),
      screen('account-ledger', 'كشف حساب', 'حساب واحد بقيوده', {
        kind: 'ledger',
        accountCode: null,
      }),
      screen('income-statement', 'قائمة الدخل', 'مع مقارنة بفترة سابقة', {
        kind: 'reports',
        tab: 'income',
      }),
      screen('balance-sheet', 'المركز المالى', 'مع مقارنة بتاريخ سابق', {
        kind: 'reports',
        tab: 'balance',
      }),
      {
        id: 'journal-register',
        title: 'سجل القيود',
        hint: 'القيود المنشورة بمصدرها ومن رحّلها',
        kind: 'center',
        report: 'journal-register',
      },
      {
        id: 'cash-flow',
        title: 'قائمة التدفقات النقدية',
        hint: 'من صافى الربح إلى التغير فى النقدية: تشغيل واستثمار وتمويل',
        kind: 'center',
        report: 'cash-flow',
      },
    ],
  },
  {
    id: 'tax',
    title: 'الضرائب',
    entries: [
      screen('vat', 'ملخص القيمة المضافة', 'المخرجات والمدخلات والصافى', {
        kind: 'reports',
        tab: 'vat',
      }),
      screen('withholding-receivable', 'الخصم من المنبع لحسابنا', 'ما خصمه العملاء', {
        kind: 'reports',
        tab: 'withholding',
      }),
      screen('withholding-payable', 'الخصم من المنبع المستحق', 'من شاشة سداد الموردين', {
        kind: 'payments',
      }),
      {
        id: 'einvoice-status',
        title: 'حالة الفاتورة الإلكترونية',
        hint: 'جاهزية كل فاتورة للمنظومة وما ينقصها — الإرسال لم يُبنَ بعد',
        kind: 'center',
        report: 'einvoice-status',
      },
    ],
  },
  {
    id: 'control',
    title: 'الرقابة',
    entries: [
      {
        id: 'audit-log',
        title: 'سجل التدقيق',
        hint: 'من فعل ماذا ومتى — للمالك ومن يدير الأعضاء',
        kind: 'center',
        report: 'audit-log',
      },
      screen('periods', 'حالة الفترات المالية', 'المفتوح والمقفل', { kind: 'periods' }),
    ],
  },
];

/** بند التقرير المبنى هنا — للعنوان والرجوع إلى فئته. */
export function centerEntry(report: CenterReport): {
  category: CatalogCategory;
  entry: CatalogEntry;
} {
  for (const category of CATALOG) {
    const entry = category.entries.find((item) => item.kind === 'center' && item.report === report);
    if (entry) return { category, entry };
  }
  throw new Error('report not in catalog: ' + report);
}

/** قيم الأعمدة النصية المرمّزة — تُعرض وتُصدَّر بعناوينها. */
export const VALUE_LABELS: Record<string, string> = {
  open: 'عليها متبقٍ',
  settled: 'مسددة',
  cancelled: 'ملغاة',
  credit_note: 'إشعار دائن',
  debit_note: 'إشعار مدين',
  posted: 'منشور',
  reversed: 'معكوس',
  stock: 'مخزنى',
  service: 'خدمة (بلا تكلفة مخزنية)',
  no_item: 'سطور بلا صنف',
  operating: 'التشغيل',
  investing: 'الاستثمار',
  financing: 'التمويل',
  ready: 'جاهزة',
  not_ready: 'ينقصها بيانات',
  company_blocked: 'ينقص بيانات الشركة أو الربط',
  not_submitted: 'لم تُرسل — الإرسال غير متاح بعد',
};

/** الكمية كما تُكتب: بلا أصفار عشرية زائدة (`12.5000` ← `12.5`). */
export function formatQuantity(value: string): string {
  return /^-?\d+\.\d+$/.test(value) ? value.replace(/\.?0+$/, '') : value;
}

export function cellText(
  column: { key: string; type: string },
  value: string | number | null | undefined,
  sourceLabels: Record<string, string>,
): string | number | null {
  if (value === null || value === undefined) return null;
  if (column.key === 'sourceType') return sourceLabels[String(value)] ?? String(value);
  if (column.type === 'text') return VALUE_LABELS[String(value)] ?? value;
  if (column.type === 'quantity') return formatQuantity(String(value));
  return value;
}

export function tabularCsv(
  report: TabularReportResponse,
  sourceLabels: Record<string, string>,
): { header: string[]; rows: CsvCell[][] } {
  const columns = REPORT_COLUMNS[report.report as TabularReport] ?? [];
  const firstMoney = columns.findIndex((column) => column.type === 'money');
  return {
    header: columns.map((column) => column.label),
    rows: [
      ...report.rows.map((row) =>
        columns.map((column) => cellText(column, row.cells[column.key], sourceLabels)),
      ),
      ...(firstMoney < 0
        ? []
        : [
            columns.map((column, index) =>
              column.type === 'money'
                ? (report.totals[column.key] ?? null)
                : index === 0
                  ? 'الإجمالى'
                  : null,
            ),
          ]),
      ...report.reconciliation.map((line) => [
        line.label,
        line.report,
        line.reference,
        line.difference,
      ]),
      ...report.notes.map((note) => [note]),
    ],
  };
}
